/**
 * An uploaded or created plate's page: one place to see it and change it.
 *
 *   ┌ top bar: back · its name and who made it · ‹ › · ★ · Package · ⋯ (downloads, remove) ┐
 *   │ the plate, live — every change shows here     │ Design · Animation · Blur ·        │
 *   │ before it is saved (or: adjust its areas)     │ AI changes · Details               │
 *   │ with sample words · play again                │ … the chosen tab                   │
 *   │                                               │ [Discard] [Save changes]           │
 *
 * Changes are a draft until saved: the preview always shows the draft; one
 * Save keeps all of it. People who can't change the plate see its preview
 * and details.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLockPageScroll } from '../../hooks/useLockPageScroll';
import { createPortal } from 'react-dom';
import {
  FiArrowLeft, FiChevronLeft, FiChevronRight, FiX, FiPackage, FiMoreHorizontal, FiTrash2, FiCheck, FiLoader, FiRotateCcw,
  FiPlay, FiType, FiFilm, FiDroplet, FiMessageSquare, FiInfo, FiMove, FiEye, FiFileText, FiMaximize, FiMinimize,
} from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { showError } from '../FullStory/shared';
import { PlateLive } from './PlateLive';
import {
  FontPicker, PromptPanel, PlateThread, BoxesEditor, PeopleSettings, MOTIONS, INKS, FX, MAX_FX, TRANSITIONS,
} from './UploadedPlates';
import { PlateDownloads, PackageDialog, BlurControls, blurBoxes } from './PlatePackage';
import { FavoriteButton } from './PlatePrompts';
import { since } from './plateTime';
import { Stage3DControls, STAGE3D_DEFAULTS } from './PlateStage3D';

const TABS = [
  ['design', FiType], ['animation', FiFilm], ['blur', FiDroplet], ['changes', FiMessageSquare], ['details', FiInfo],
];

/** The draft: everything about the plate that can be changed here, from the plate as saved. */
const draftOf = (p) => ({
  name: p.name || '', category: p.category || 'general', box: p.box, ink: p.ink || 'auto',
  font: p.font || 'pallavi', effect: p.effect || 'flat', motion: p.motion || 'push', dust: Boolean(p.dust),
  fx: p.fx || [], fxLayer: p.fxLayer || 'front', transition: p.transition || 'build', blurs: p.blurs || [],
  // 3D (off unless switched on; its settings kept when off).
  stage3d: { ...STAGE3D_DEFAULTS, ...(p.stage3d || {}) },
  people: {
    motion: p.people?.motion || 'breathe', entrance: p.people?.entrance || 'slide',
    strength: p.people?.strength || 'medium', enabled: p.people?.enabled !== false,
  },
});

/** A row of choices; the chosen one filled. */
const Choices = ({ values, value, onChange, label, disabledFor }) => (
  <div className="flex flex-wrap gap-1.5">
    {values.map(([v, text]) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        aria-pressed={value === v}
        disabled={disabledFor?.(v)}
        title={label}
        className={`px-2.5 py-1 rounded-full text-xs font-medium border disabled:opacity-40 ${value === v ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`}
      >
        {text}
      </button>
    ))}
  </div>
);

/** A titled block in a tab. */
const Section = ({ title, hint, action, children }) => (
  <section className="space-y-2">
    <div className="flex items-start justify-between gap-2">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

/** The "⋯" menu: downloads and remove — things not needed every time. */
const MoreMenu = ({ template, canRemove, onRemove }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={t('plateTemplates.studio.more')} title={t('plateTemplates.studio.more')} className="btn btn-ghost btn-sm px-2">
        <FiMoreHorizontal size={18} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-80 rounded-xl border border-gray-200 bg-white shadow-xl p-3 space-y-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{t('plateTemplates.studio.downloads')}</p>
            <PlateDownloads template={template} />
          </div>
          {canRemove && (
            <button type="button" onClick={() => { setOpen(false); onRemove(); }} className="w-full btn btn-ghost btn-sm justify-start text-red-600 flex items-center gap-1.5 border-t border-gray-100 pt-2 rounded-none">
              <FiTrash2 size={14} /> {t('plateTemplates.studio.remove')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PlateStudio = ({
  template, category, categories = [], canEdit, onSaved, onRemove, onReload, onClose, onStep, renderTags, onOpenPrompts,
}) => {
  useLockPageScroll();
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [draft, setDraft] = useState(() => draftOf(template));
  const [tab, setTab] = useState(canEdit ? 'design' : 'details');
  const [mode, setMode] = useState('live'); // live | areas
  const [words, setWords] = useState(true);
  const [replay, setReplay] = useState(0);
  const [saving, setSaving] = useState(false);
  const [packaging, setPackaging] = useState(false);
  // The preview full screen (Esc, or the button, to leave).
  const stage = useRef(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === stage.current && Boolean(stage.current));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else stage.current?.requestFullscreen?.().catch(() => {});
  };
  const saved = useMemo(() => draftOf(template), [template]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const peopleFound = Boolean(template.people?.status === 'ready' && template.people?.current);
  const set = (change) => setDraft((d) => ({ ...d, ...change }));
  const [writing, setWriting] = useState(false);
  const writePrompt = async () => {
    setWriting(true);
    try { await api.writePlatePrompt(template.id); await onReload(); } catch (e) { showError(e); } finally { setWriting(false); }
  };

  // Another plate (‹ ›), or this one saved: start from it as it is.
  useEffect(() => { setDraft(draftOf(template)); setMode('live'); }, [template.id, template.updatedAt]); // eslint-disable-line
  useEffect(() => { setTab(canEdit ? 'design' : 'details'); setPackaging(false); }, [template.id, canEdit]);

  const leave = useCallback((go) => {
    if (dirty && !window.confirm(t('plateTemplates.studio.discardConfirm'))) return;
    go();
  }, [dirty, t]);

  useEffect(() => {
    const onKey = (e) => {
      const el = e.target;
      if (packaging) return;
      if (el?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName)) return;
      if (e.key === 'Escape') leave(onClose);
      if (e.key === 'ArrowLeft') leave(() => onStep(-1));
      if (e.key === 'ArrowRight') leave(() => onStep(1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep, packaging, leave]);

  const save = async () => {
    setSaving(true);
    try {
      const { people, ...rest } = draft;
      onSaved(await api.updatePlateTemplate(template.id, { ...rest, ...(peopleFound ? { people } : {}) }));
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  // The preview: the draft, live (changes show before they are saved).
  const look = {
    box: draft.box, ink: draft.ink, font: draft.font, effect: draft.effect, blurs: draft.blurs,
    motion: draft.motion, dust: draft.dust, fx: draft.fx, fxLayer: draft.fxLayer, transition: draft.transition,
    ...(peopleFound ? { people: draft.people } : {}),
    stage3d: draft.stage3d,
  };
  const autoInk = t(`plateTemplates.ink.${template.autoInk === 'dark' ? 'dark' : 'light'}`);
  const canChange = Boolean(template.created && canEdit && template.status === 'ready');
  const tabs = TABS.filter(([id]) => (id === 'changes' ? canChange : id === 'details' || canEdit));
  const poster = api.plateTemplateUrl(template.id, 'poster.jpg', template.updatedAt);

  // The Animation, Blur and AI changes tabs: here, and in the package window
  // (onTry: play the change; onAreas: show the areas to move).
  const renderTab = (id, { onTry, onAreas }) => (
    <div className="space-y-6">
      {id === 'animation' && (
        <>
                <Stage3DControls plateId={template.id} value={draft.stage3d} onChange={(stage3d) => set({ stage3d })} onTry={onTry} />
                {draft.stage3d.on && <p className="text-[11px] text-gray-500 -mt-3">{t('plateTemplates.s3.replaces')}</p>}
                <Section title={t('plateTemplates.editMotion')}>
                  <Choices values={MOTIONS.map((v) => [v, t(`plateTemplates.motion.${v}`)])} value={draft.motion} onChange={(v) => set({ motion: v })} />
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="checkbox" checked={draft.dust} onChange={(e) => set({ dust: e.target.checked })} className="rounded border-gray-300" /> {t('plateTemplates.editDust')}
                  </label>
                </Section>
                <Section title={t('plateTemplates.transition.title')} hint={t('plateTemplates.transition.hint')}>
                  <Choices values={TRANSITIONS.map((v) => [v, t(`plateTemplates.transition.${v}`)])} value={draft.transition} onChange={(v) => { set({ transition: v }); onTry(); }} />
                </Section>
                <Section title={t('plateTemplates.fx.title')} hint={t('plateTemplates.fx.hint', { count: draft.fx.length, max: MAX_FX })}>
                  <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('plateTemplates.fx.title')}>
                    {FX.map((id) => {
                      const on = draft.fx.includes(id);
                      return (
                        <button key={id} type="button" aria-pressed={on} disabled={!on && draft.fx.length >= MAX_FX}
                          onClick={() => set({ fx: on ? draft.fx.filter((x) => x !== id) : [...draft.fx, id] })}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border disabled:opacity-40 ${on ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`}
                        >
                          {t(`plateTemplates.fx.${id}`)}
                        </button>
                      );
                    })}
                  </div>
                  {(draft.fx.length > 0 || draft.dust) && (
                    <div className="pt-1 space-y-1">
                      <p className="text-xs text-gray-600">{t('plateTemplates.fxLayer.title')}</p>
                      <Choices values={['front', 'behind'].map((v) => [v, t(`plateTemplates.fxLayer.${v}`)])} value={draft.fxLayer} onChange={(v) => { set({ fxLayer: v }); onTry(); }} />
                      <p className="text-[11px] text-gray-500">{t(`plateTemplates.fxLayer.hint.${draft.fxLayer}`)}</p>
                    </div>
                  )}
                </Section>
                <PeopleSettings
                  plate={template}
                  people={template.people}
                  look={draft.people}
                  onLook={(change) => set({ people: { ...draft.people, ...change } })}
                  onFound={() => onReload()}
                  onTry={onTry}
                />
              </>
            )}

            {id === 'blur' && (
              <Section title={t('plateTemplates.studio.blurTitle')} hint={t('plateTemplates.blur.plateHint')}>
                <BlurControls blurs={draft.blurs} onChange={(blurs) => set({ blurs })} onAdded={onAreas} />
              </Section>
            )}

            {id === 'changes' && <PlateThread plate={template} onChanged={onReload} />}

    </div>
  );

  // On the page itself (a portal): above the app's own bars, whatever the page around it.
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-gray-50 flex flex-col" role="dialog" aria-modal="true" aria-label={template.name}>
      {/* Top bar: what this is, and the few things done with it. */}
      <header className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
        <button type="button" onClick={() => leave(onClose)} className="btn btn-ghost btn-sm flex items-center gap-1.5 text-gray-600">
          <FiArrowLeft size={16} /> <span className="hidden sm:inline">{t('plateTemplates.studio.back')}</span>
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-primary-600 truncate">
            {lang === 'te' ? category?.te : category?.name}
            <span className="ml-2 rounded-full bg-sky-100 text-sky-700 px-2 py-0.5">{t(template.created ? 'plateTemplates.createdBadge' : 'plateTemplates.uploadedBadge')}</span>
          </p>
          <h1 className="text-base font-bold text-gray-900 truncate">{template.name}</h1>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => leave(() => onStep(-1))} aria-label={t('plateTemplates.previous')} className="btn btn-ghost btn-sm px-2"><FiChevronLeft size={18} /></button>
          <button type="button" onClick={() => leave(() => onStep(1))} aria-label={t('plateTemplates.next')} className="btn btn-ghost btn-sm px-2"><FiChevronRight size={18} /></button>
        </div>
        <div className="flex items-center gap-1.5">
          {(template.created ? template.status === 'ready' : template.hasPrompt) && <FavoriteButton plateId={template.id} onOpenLibrary={onOpenPrompts && (() => leave(onOpenPrompts))} />}
          <button type="button" onClick={() => setPackaging(true)} className="btn btn-outline btn-sm flex items-center gap-1.5">
            <FiPackage size={15} /> {t('plateTemplates.pkg.open')}
          </button>
          <MoreMenu template={template} canRemove={canEdit} onRemove={onRemove} />
          <button type="button" onClick={() => leave(onClose)} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm px-2"><FiX size={18} /></button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* The plate: live, as it will be (or: its areas, to move). */}
        <main className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-5xl mx-auto space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div role="tablist" aria-label={t('plateTemplates.studio.previewMode')} className="inline-flex p-0.5 rounded-lg bg-gray-200/70 text-xs font-medium">
                <button type="button" role="tab" aria-selected={mode === 'live'} onClick={() => setMode('live')} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${mode === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                  <FiEye size={13} /> {t('plateTemplates.studio.live')}
                </button>
                {canEdit && (
                  <button type="button" role="tab" aria-selected={mode === 'areas'} onClick={() => setMode('areas')} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${mode === 'areas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                    <FiMove size={13} /> {t('plateTemplates.studio.areas')}
                  </button>
                )}
              </div>
              {mode === 'live' && (
                <div className="flex items-center gap-3 text-xs text-gray-700">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={words} onChange={(e) => setWords(e.target.checked)} className="rounded border-gray-300" />
                    {t('plateTemplates.studio.sampleWords')}
                  </label>
                  <button type="button" onClick={() => setReplay((k) => k + 1)} className="btn btn-ghost btn-sm flex items-center gap-1"><FiPlay size={13} /> {t('plateTemplates.studio.replay')}</button>
                  <button type="button" onClick={toggleFull} className="btn btn-ghost btn-sm flex items-center gap-1"><FiMaximize size={13} /> {t('plateTemplates.studio.fullscreen')}</button>
                </div>
              )}
            </div>
            {mode === 'live' ? (
              <div ref={stage} onDoubleClick={toggleFull} className={`group relative overflow-hidden bg-black ${full ? 'w-screen h-screen' : 'aspect-video rounded-xl shadow-sm'}`}>
                <PlateLive key={template.id} replay={replay} plateId={template.id} words={words} look={look} version={`${template.updatedAt}|${template.people?.rev || 0}`} poster={poster} />
                {dirty && <span className="absolute top-2 left-2 rounded-full bg-amber-500 text-white text-[11px] font-medium px-2.5 py-0.5 shadow">{t('plateTemplates.studio.unsaved')}</span>}
                <button type="button" onClick={toggleFull} aria-label={t(full ? 'plateTemplates.studio.exitFullscreen' : 'plateTemplates.studio.fullscreen')} title={t(full ? 'plateTemplates.studio.exitFullscreen' : 'plateTemplates.studio.fullscreen')} className={`absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/75 ${full ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} transition-opacity`}>
                  {full ? <FiMinimize size={16} /> : <FiMaximize size={16} />}
                </button>
              </div>
            ) : (
              <BoxesEditor
                src={api.plateTemplateUrl(template.id, 'source.jpg', template.updatedAt)}
                boxes={[{ box: draft.box, label: t('plateTemplates.textArea') }, ...blurBoxes(draft.blurs, t)]}
                onChange={(i, next) => (i === 0 ? set({ box: next }) : set({ blurs: draft.blurs.map((b, k) => (k === i - 1 ? { ...b, ...next } : b)) }))}
              />
            )}
            <p className="text-[11px] text-gray-500">{t(mode === 'live' ? 'plateTemplates.studio.liveHint' : 'plateTemplates.studio.areasHint')}</p>
          </div>
        </main>

        {/* Changing it: one tab at a time. */}
        <aside className="lg:w-[440px] flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col min-h-0">
          <nav role="tablist" className="grid px-2 pt-2 border-b border-gray-100" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.map(([id, Icon]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-1 py-2 text-[11px] sm:text-xs font-medium border-b-2 -mb-px whitespace-nowrap ${tab === id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                <Icon size={13} /> {t(`plateTemplates.studio.tab.${id}`)}
              </button>
            ))}
          </nav>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {tab === 'design' && (
              <>
                <Section
                  title={t('plateTemplates.textArea')}
                  hint={t('plateTemplates.studio.textAreaHint')}
                  action={<button type="button" onClick={() => setMode(mode === 'areas' ? 'live' : 'areas')} className="btn btn-outline btn-sm flex items-center gap-1"><FiMove size={13} /> {t(mode === 'areas' ? 'plateTemplates.studio.done' : 'plateTemplates.studio.move')}</button>}
                />
                <Section title={t('plateTemplates.editInk')}>
                  <Choices values={INKS.map((v) => [v, v === 'auto' ? t('plateTemplates.ink.auto', { ink: autoInk }) : t(`plateTemplates.ink.${v}`)])} value={draft.ink} onChange={(v) => set({ ink: v })} />
                </Section>
                <FontPicker font={draft.font} effect={draft.effect} onChange={(change) => set(change)} />
              </>
            )}

            {['animation', 'blur', 'changes'].includes(tab) && renderTab(tab, {
              onTry: () => { setMode('live'); setReplay((k) => k + 1); },
              onAreas: () => setMode('areas'),
            })}

            {tab === 'details' && (
              <>
                {canEdit ? (
                  <div className="grid grid-cols-1 gap-3">
                    <label className="block text-sm">
                      <span className="text-gray-700 font-medium">{t('plateTemplates.editName')}</span>
                      <input value={draft.name} onChange={(e) => set({ name: e.target.value })} maxLength={60} className="input mt-1 text-sm" />
                    </label>
                    <label className="block text-sm">
                      <span className="text-gray-700 font-medium">{t('plateTemplates.uploadCategory')}</span>
                      <select value={draft.category} onChange={(e) => set({ category: e.target.value })} className="input mt-1 text-sm">
                        {categories.map((c) => <option key={c.id} value={c.id}>{lang === 'te' ? c.te : c.name}</option>)}
                      </select>
                    </label>
                  </div>
                ) : null}
                <Section title={t('plateTemplates.studio.tags')}>{renderTags?.()}</Section>
                <Section title={t('plateTemplates.studio.about')}>
                  <dl className="text-xs text-gray-600 space-y-1">
                    {template.createdByName && <div>{t('plateTemplates.by', { name: template.createdByName })}</div>}
                    {template.createdAt && <div>{t('plateTemplates.card.created', { at: new Date(template.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}</div>}
                    {template.updatedAt && template.updatedAt !== template.createdAt && <div>{t('plateTemplates.card.modified', { at: new Date(template.updatedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}</div>}
                    {template.created && template.readyAt && template.designStartedAt && (
                      <div className="text-emerald-700 tabular-nums">
                        {t('plateTemplates.times.ready', { time: since(template.designStartedAt, Date.parse(template.readyAt)) })}
                        {template.animatedAt ? ` · ${t('plateTemplates.times.animated', { time: since(template.designStartedAt, Date.parse(template.animatedAt)) })}` : ''}
                      </div>
                    )}
                    <div className="font-mono text-gray-400">{template.id}</div>
                  </dl>
                </Section>
                {template.created && template.references > 0 && (
                  <Section title={t('plateTemplates.createReferencesUsed')}>
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from({ length: template.references }, (_, k) => (
                        <a key={k} href={api.plateTemplateUrl(template.id, `ref-create-${k + 1}.jpg`)} target="_blank" rel="noreferrer">
                          <img src={api.plateTemplateUrl(template.id, `ref-create-${k + 1}.jpg`)} alt="" loading="lazy" className="h-12 w-12 rounded object-cover border border-gray-200" />
                        </a>
                      ))}
                    </div>
                  </Section>
                )}
                <Section title={t('plateTemplates.viewPrompt')} action={<FiFileText size={14} className="text-gray-400" />}>
                  {template.hasPrompt ? <PromptPanel templateId={template.id} /> : template.promptStatus === 'writing' ? (
                    <p className="text-xs text-gray-600 inline-flex items-center gap-1.5"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.writingPrompt')}</p>
                  ) : canEdit && !template.created ? (
                    <button type="button" onClick={writePrompt} disabled={writing} className="btn btn-outline btn-sm flex items-center gap-1">{writing ? <FiLoader size={13} className="animate-spin" /> : <FiFileText size={13} />} {t('plateTemplates.writePrompt')}</button>
                  ) : <p className="text-xs text-gray-500">{t('plateTemplates.studio.noPrompt')}</p>}
                </Section>
              </>
            )}
          </div>

          {/* One place to keep the changes. */}
          {canEdit && tab !== 'changes' && (
            <footer className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-white">
              {dirty ? (
                <>
                  <button type="button" onClick={() => setDraft(saved)} disabled={saving} className="btn btn-ghost btn-sm flex items-center gap-1"><FiRotateCcw size={13} /> {t('plateTemplates.studio.discard')}</button>
                  <button type="button" onClick={save} disabled={saving || !draft.name.trim()} className="btn btn-primary flex items-center gap-2">
                    {saving ? <FiLoader size={14} className="animate-spin" /> : <FiCheck size={14} />} {t(saving ? 'plateTemplates.saving' : 'plateTemplates.studio.save')}
                  </button>
                </>
              ) : (
                <span className="text-xs text-gray-500 inline-flex items-center gap-1.5"><FiCheck size={13} className="text-emerald-600" /> {t('plateTemplates.studio.allSaved')}</span>
              )}
            </footer>
          )}
        </aside>
      </div>

      {packaging && (
        <PackageDialog
          plate={template}
          onClose={() => setPackaging(false)}
          // The plate's own settings in the package window too, with the same draft (and Save).
          studio={canEdit ? {
            look, dirty, saving, canSave: Boolean(draft.name.trim()), save, discard: () => setDraft(saved),
            tabs: tabs.filter(([id]) => ['animation', 'blur', 'changes'].includes(id)), renderTab,
            plateBlurs: draft.blurs, setPlateBlurs: (blurs) => set({ blurs }),
          } : null}
        />
      )}
    </div>,
    document.body,
  );
};

export default PlateStudio;
